import { useEffect, useRef, useState } from "react";
import { RADAR_FAMILIES, type RunActivity, type RunActivityEvent } from "@shared/lib/run-activity-shared";
import { cn } from "@shared/lib/utils";
import { useT } from "@shared/client/locale";
import type { Messages } from "@shared/i18n";

/**
 * Le radar d'un passage : ce que l'agent fait vraiment, lu dans ses traces.
 *
 * Les huit branches de l'astérisque du logo sont les huit familles de sources
 * (réseau pro, emploi, appels d'offres…). Chaque recherche part du centre le
 * long de sa branche, chaque page lue s'y pose en point gris. Quand le
 * rapport tombe, les organisations retenues rejoignent l'anneau intérieur en
 * bleu, puis un anneau se referme autour de chacune quand l'enrichissement
 * s'en occupe. Rien n'est simulé : pas de pourcentage, seulement ce que les
 * traces disent.
 */

const POLL_MS = 3_000;
const R_IN = 34;
const R_OUT = 160;
const R_LEADS = 52;
const MAX_LEADS = 16;
const PHASE_STEP = { queued: 1, understand: 1, explore: 2, retain: 3, contacts: 4, done: 5, failed: 0 } as const;

const radians = (family: number) => ((-90 + family * 45) * Math.PI) / 180;

function point(family: number, radius: number, jitter = 0): [number, number] {
  const angle = radians(family) + jitter;
  return [Math.cos(angle) * radius, Math.sin(angle) * radius];
}

/** Deux nombres stables dans [0, 1[ tirés de l'identifiant : un point ne bouge pas d'un rafraîchissement à l'autre. */
function hashPair(id: string): [number, number] {
  let a = 2166136261;
  let b = 5381;
  for (let i = 0; i < id.length; i++) {
    const code = id.charCodeAt(i);
    a = Math.imul(a ^ code, 16777619);
    b = (Math.imul(b, 33) + code) | 0;
  }
  return [((a >>> 0) % 10_000) / 10_000, ((b >>> 0) % 10_000) / 10_000];
}

function dotPosition(event: RunActivityEvent): [number, number] {
  const [h1, h2] = hashPair(event.id);
  return point(event.family, 66 + h1 * 90, (h2 - 0.5) * 0.55);
}

function leadPosition(index: number, total: number): [number, number] {
  const angle = ((-90 + (360 / total) * index) * Math.PI) / 180;
  return [Math.cos(angle) * R_LEADS, Math.sin(angle) * R_LEADS];
}

/** Un lead : il part d'une page lue et rejoint l'anneau intérieur. */
function LeadDot({ from, to, delay }: { from: [number, number]; to: [number, number]; delay: number }) {
  const ref = useRef<SVGCircleElement>(null);
  const [dx, dy] = [from[0] - to[0], from[1] - to[1]];
  // Une seule fois, à l'arrivée du lead : les relectures suivantes ne le rejouent pas.
  useEffect(() => {
    const node = ref.current;
    if (!node || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const animation = node.animate(
      [
        { transform: `translate(${dx}px, ${dy}px)`, opacity: 0.4 },
        { transform: "translate(0, 0)", opacity: 1 },
      ],
      { duration: 900, delay, easing: "cubic-bezier(.3,.7,.2,1)", fill: "backwards" },
    );
    return () => animation.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- animation d'entrée uniquement
  }, []);
  return <circle className="fill-chart-1" cx={to[0]} cy={to[1]} r={5} ref={ref} />;
}

function nowLine(activity: RunActivity | null, copy: Messages["dashboard"]["radar"]["now"]): string {
  if (!activity) return copy.connecting;
  const last = activity.events.at(-1);
  switch (activity.phase) {
    case "queued":
      return copy.queued;
    case "understand":
      return copy.understand;
    case "explore":
      if (last?.type === "search" && last.label) return copy.searching(last.label);
      if (last?.type === "read" && last.label) return copy.reading(last.label);
      return copy.exploring;
    case "retain":
      return activity.status === "completed" ? copy.filing : copy.writing;
    case "contacts":
      return copy.contacts;
    case "done":
      return activity.leads > 0 ? copy.leads(activity.leads) : copy.nothingNew;
    case "failed":
      // The stored error of the pass (written by the worker), else a generic line.
      return activity.error ?? copy.failed;
  }
}

export function AgentRadar({
  runId,
  onSettled,
  className,
}: {
  runId: string;
  /** Appelé une fois, quand le passage est terminé (interlocuteurs compris) ou interrompu. */
  onSettled?: (activity: RunActivity) => void;
  className?: string;
}) {
  const copy = useT().dashboard.radar;
  const [activity, setActivity] = useState<RunActivity | null>(null);
  const [fresh, setFresh] = useState<string[]>([]);
  const settledRef = useRef(onSettled);

  useEffect(() => {
    settledRef.current = onSettled;
  }, [onSettled]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let known = new Set<string>();

    async function poll() {
      try {
        const response = await fetch(`/api/runs/${runId}/activity`);
        if (response.ok) {
          const next = (await response.json()) as RunActivity;
          if (cancelled) return;
          // Les recherches arrivées depuis la dernière lecture lancent leur sonde.
          setFresh(next.events.filter((event) => event.type === "search" && !known.has(event.id)).map((event) => event.id));
          known = new Set(next.events.map((event) => event.id));
          setActivity(next);
          if (next.phase === "done" || next.phase === "failed") {
            settledRef.current?.(next);
            return;
          }
        }
      } catch {
        // Réseau capricieux : on relit au battement suivant.
      }
      if (!cancelled) timer = setTimeout(poll, POLL_MS);
    }

    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [runId]);

  const phase = activity?.phase ?? "queued";
  const step = PHASE_STEP[phase];
  const events = activity?.events ?? [];
  const last = events.at(-1);
  const activeFamily = phase === "explore" && last ? last.family : -1;
  const leadCount = Math.min(activity?.retained ?? 0, MAX_LEADS);
  const showLeads = leadCount > 0 && (phase === "retain" || phase === "contacts" || phase === "done");
  const reads = events.filter((event) => event.type === "read");
  const freshProbes = events.filter((event) => fresh.includes(event.id)).slice(-6);

  const steps = [
    { label: copy.steps.understand, note: phase === "queued" ? copy.notes.queued : "" },
    { label: copy.steps.explore, note: activity?.searches ? copy.searches(activity.searches) : "" },
    {
      label: copy.steps.retain,
      note: activity?.retained
        ? copy.notes.retained(activity.retained)
        : phase === "retain"
          ? copy.notes.writing
          : "",
    },
    {
      label: copy.steps.contacts,
      note: step >= 4 && activity?.retained ? copy.notes.enriched(activity.enriched, activity.retained) : "",
    },
  ];
  const line = nowLine(activity, copy.now);

  return (
    <div className={cn("grid md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]", className)}>
      <div className="grid place-items-center overflow-hidden border-b p-2 md:border-r md:border-b-0">
        {/* Sous `sm`, sans libellés, le radar est recadré sur ses anneaux. */}
        <svg
          aria-label={copy.label(activity?.searches ?? 0, activity?.pages ?? 0)}
          className="block h-auto w-full max-w-[480px] overflow-visible max-sm:-my-[14%] max-sm:w-[150%] max-sm:max-w-none"
          role="img"
          viewBox="-275 -212 550 424"
        >
          {[68, 114, 160].map((radius) => (
            <circle className="fill-none stroke-border" key={radius} r={radius} />
          ))}
          {RADAR_FAMILIES.map((id, family) => {
            const [x1, y1] = point(family, R_IN);
            const [x2, y2] = point(family, R_OUT);
            const [lx, ly] = point(family, 180);
            const anchor = Math.abs(lx) < 8 ? "middle" : lx > 0 ? "start" : "end";
            const counts = activity?.families[family];
            const count = counts?.pages
              ? copy.pages(counts.pages)
              : counts?.searches
                ? copy.searches(counts.searches)
                : "";
            return (
              <g key={id}>
                <line
                  className={cn("transition-colors duration-300", family === activeFamily ? "stroke-[#c9c9d1]" : "stroke-border")}
                  x1={x1}
                  x2={x2}
                  y1={y1}
                  y2={y2}
                />
                <g className="max-sm:hidden">
                  <text
                    className={cn("text-[14.5px] transition-colors", family === activeFamily ? "fill-foreground" : "fill-subtle-foreground")}
                    textAnchor={anchor}
                    x={lx}
                    y={ly + 4}
                  >
                    {copy.families[id]}
                  </text>
                  <text className="fill-subtle-foreground text-[13.5px] tabular-nums" textAnchor={anchor} x={lx} y={ly + 21}>
                    {count}
                  </text>
                </g>
              </g>
            );
          })}

          <g className={cn("transition-opacity duration-700", showLeads && "opacity-35")}>
            {events.map((event) => {
              const [cx, cy] = dotPosition(event);
              return event.type === "read" ? (
                <circle className="radar-pop fill-[#b4b4bd]" cx={cx} cy={cy} key={event.id} r={3.2} />
              ) : (
                <circle className="radar-pop fill-none stroke-[#c9c9d1]" cx={cx} cy={cy} key={event.id} r={2.4} strokeWidth={1.2} />
              );
            })}
          </g>

          {freshProbes.map((event, index) => (
            <g key={`probe-${event.id}`} transform={`rotate(${-90 + event.family * 45})`}>
              <circle
                className="radar-probe fill-foreground"
                r={2.8}
                style={{ "--from": `${R_IN}px`, "--to": `${R_OUT}px`, animationDelay: `${index * 350}ms` } as React.CSSProperties}
              />
            </g>
          ))}

          {phase === "retain" && activity?.status !== "completed" && (
            <circle className="radar-spin-fast fill-none stroke-subtle-foreground" r={R_LEADS} strokeDasharray="3 7" strokeWidth={1.2} />
          )}

          {showLeads &&
            Array.from({ length: leadCount }, (_, index) => {
              const to = leadPosition(index, leadCount);
              const source = reads[(index * 7) % Math.max(reads.length, 1)];
              const from = source ? dotPosition(source) : point(index % 8, 120);
              const length = 2 * Math.PI * 9;
              return (
                <g key={`lead-${index}`}>
                  <LeadDot delay={index * 120} from={from} to={to} />
                  {index < (activity?.enriched ?? 0) && (
                    <circle
                      className="radar-ring fill-none stroke-chart-1"
                      cx={to[0]}
                      cy={to[1]}
                      r={9}
                      strokeDasharray={length}
                      strokeWidth={1.4}
                      style={{ "--length": length } as React.CSSProperties}
                      transform={`rotate(-90 ${to[0]} ${to[1]})`}
                    />
                  )}
                </g>
              );
            })}

          {/* Le cœur : l'astérisque du logo. */}
          {(phase === "queued" || phase === "understand") && (
            <circle className="radar-halo fill-none stroke-primary" r={26} strokeOpacity={0.5} />
          )}
          <circle className="fill-background stroke-border" r={27} />
          <g className={phase === "done" || phase === "failed" ? undefined : "radar-spin"}>
            {[0, 45, 90, 135].map((angle) => (
              <rect className="fill-primary" height={34} key={angle} rx={1} transform={`rotate(${angle})`} width={6.4} x={-3.2} y={-17} />
            ))}
          </g>
        </svg>
      </div>

      <div className="grid content-start gap-5 px-6 py-5">
        <ol className="m-0 grid list-none gap-0.5 p-0" data-tour="agent-steps">
          {steps.map((item, index) => {
            const state = phase === "failed" ? "idle" : index + 1 < step ? "done" : index + 1 === step ? "active" : "idle";
            return (
              <li
                className={cn(
                  "grid grid-cols-[18px_1fr_auto] items-center gap-2.5 py-1.5 text-[13.5px]",
                  state === "idle" && "text-subtle-foreground",
                  state === "done" && "text-muted-foreground",
                  state === "active" && "font-medium text-foreground",
                )}
                key={item.label}
              >
                <i
                  aria-hidden="true"
                  className={cn(
                    "relative size-3.5 rounded-full border-[1.5px] border-[#e9e9ee]",
                    state === "done" && "border-foreground bg-foreground",
                    state === "active" && "animate-spin border-foreground border-r-transparent motion-reduce:animate-none",
                  )}
                >
                  {state === "done" && (
                    <span className="absolute top-[1.5px] left-[3.5px] h-1.5 w-[3px] rotate-45 border-r-[1.5px] border-b-[1.5px] border-background" />
                  )}
                </i>
                {item.label}
                <small className="text-[12.5px] font-normal text-subtle-foreground tabular-nums">{item.note}</small>
              </li>
            );
          })}
        </ol>

        <div className="grid grid-cols-3 overflow-hidden rounded-lg border">
          {[
            { label: copy.totals.searches, value: activity?.searches ?? 0 },
            { label: copy.totals.pages, value: activity?.pages ?? 0 },
            { label: copy.totals.leads, value: activity?.leads ?? 0 },
          ].map((item) => (
            <div className="border-r px-3 py-2.5 last:border-r-0" key={item.label}>
              <strong className="block text-[20px] leading-tight font-semibold tracking-[-0.02em] tabular-nums">
                {item.value}
              </strong>
              <span className="text-[12px] text-muted-foreground">{item.label}</span>
            </div>
          ))}
        </div>

        {/* Sous `sm`, les libellés du radar seraient trop petits : la répartition passe ici. */}
        <ul className="m-0 grid list-none grid-cols-2 gap-x-4 gap-y-1 p-0 text-[12.5px] text-muted-foreground sm:hidden">
          {RADAR_FAMILIES.map((id, family) => (
            <li className="flex justify-between gap-2" key={id}>
              <span className={cn(family === activeFamily && "text-foreground")}>{copy.families[id]}</span>
              <span className="tabular-nums">{activity?.families[family]?.pages ?? 0}</span>
            </li>
          ))}
        </ul>

        <div aria-live="polite" className="min-h-11 border-t pt-4 text-[13.5px] text-muted-foreground">
          <b className="mb-0.5 block text-[12.5px] font-medium text-foreground">{phase === "done" ? copy.outcome : copy.current}</b>
          <span className="block truncate" key={line}>
            {line}
          </span>
        </div>
      </div>
    </div>
  );
}
